import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { log } from "@/lib/log";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, ArrowLeft, Loader2, FileCheck, Camera } from "lucide-react";

export default function NewExpense() {
  const navigate = useNavigate();
  const initialCount = Math.min(6, Math.max(2, parseInt(localStorage.getItem("last_approver_count") || "3", 10)));
  const [form, setForm] = useState({
    payee: "",
    amount: "",
    description: "",
    category: "",
    finance_email: localStorage.getItem("last_finance_email") || "",
    accounts_software_email: localStorage.getItem("last_accounts_software_email") || "",
  });
  const [approvers, setApprovers] = useState(Array(initialCount).fill(""));
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/categories");
        setCategories(data);
      } catch (err) {
        log.error("Failed to load categories:", err);
      }
    })();
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/templates");
      setTemplates(data);
    } catch (err) {
      log.error("Failed to load templates:", err);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const saveTemplate = async () => {
    const cleaned = approvers.map((e) => e.trim().toLowerCase()).filter(Boolean);
    if (cleaned.length < 2) return setError("Add at least 2 approvers before saving a template");
    const name = window.prompt("Template name (e.g. 'Board sign-off')");
    if (!name) return;
    try {
      const { data } = await api.post("/templates", { name: name.trim(), emails: cleaned });
      setTemplates((list) => [...list.filter((t) => t.name !== data.name), data].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      setError(formatApiError(err));
    }
  };
  const applyTemplate = (t) => setApprovers([...t.emails]);
  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/templates/${id}`);
      setTemplates((list) => list.filter((t) => t.id !== id));
    } catch (err) {
      setError(formatApiError(err));
    }
  };
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const MAX_RECEIPTS = 5;
  const ALLOWED = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

  const addFiles = (fileList) => {
    setError("");
    const incoming = Array.from(fileList || []);
    const next = [...files];
    for (const f of incoming) {
      if (!ALLOWED.includes(f.type)) { setError(`Unsupported file type: ${f.name}`); continue; }
      if (f.size > 10 * 1024 * 1024) { setError(`${f.name} exceeds the 10 MB limit`); continue; }
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
      next.push(f);
    }
    if (next.length > MAX_RECEIPTS) {
      setError(`You can upload at most ${MAX_RECEIPTS} receipts`);
      setFiles(next.slice(0, MAX_RECEIPTS));
    } else {
      setFiles(next);
    }
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (files.length === 0) return setError("Please upload at least one receipt");
    if (files.length > MAX_RECEIPTS) return setError(`You can upload at most ${MAX_RECEIPTS} receipts`);

    const cleaned = approvers.map((e) => e.trim().toLowerCase()).filter(Boolean);
    if (cleaned.length < 2 || cleaned.length > 6) return setError("Provide between 2 and 6 approver emails");
    if (new Set(cleaned).size !== cleaned.length) return setError("Approver emails must be unique");

    const fd = new FormData();
    fd.append("payee", form.payee.trim());
    fd.append("amount", form.amount);
    fd.append("currency", "GBP");
    fd.append("description", form.description.trim());
    fd.append("approver_emails", JSON.stringify(cleaned));
    fd.append("finance_email", form.finance_email.trim().toLowerCase());
    if (form.category) fd.append("category", form.category);
    if (form.accounts_software_email.trim()) {
      fd.append("accounts_software_email", form.accounts_software_email.trim().toLowerCase());
    }
    files.forEach((f) => fd.append("receipts", f));

    setBusy(true);
    try {
      const { data } = await api.post("/expenses", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      localStorage.setItem("last_finance_email", form.finance_email.trim().toLowerCase());
      localStorage.setItem("last_accounts_software_email", form.accounts_software_email.trim().toLowerCase());
      localStorage.setItem("last_approver_count", String(cleaned.length));
      navigate(`/expenses/${data.id}`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <Button asChild variant="ghost" size="sm" className="text-ink-soft hover:text-forest-800 -ml-2" data-testid="back-to-dashboard">
          <Link to="/"><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to expenses</Link>
        </Button>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 animate-fade-up">
        <div className="lg:col-span-7">
          <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">New submission</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-forest-900 tracking-tight mb-2">Submit an expense</h1>
          <p className="text-sm text-ink-soft mb-8 max-w-lg">
            Upload the receipt and nominate 2–6 approvers. Every approver must approve before this expense is marked Approved.
          </p>

          <form onSubmit={onSubmit} className="space-y-6" data-testid="new-expense-form">
            <Field label="Payee / supplier">
              <Input
                required value={form.payee} onChange={set("payee")}
                placeholder="e.g. Acme Catering Ltd"
                data-testid="payee-input"
                className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700"
              />
            </Field>

            <Field label="Amount (GBP)">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft font-medium">£</span>
                <Input
                  required type="number" min="0.01" step="0.01"
                  value={form.amount} onChange={set("amount")}
                  placeholder="0.00"
                  data-testid="amount-input"
                  className="pl-7 bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700"
                />
              </div>
            </Field>

            <Field label="Category (optional)">
              <select
                value={form.category}
                onChange={set("category")}
                data-testid="category-select"
                className="w-full h-10 rounded-md border border-cream-200 bg-white px-3 text-sm text-ink focus:outline-none focus:border-forest-700"
              >
                <option value="">— No category —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </Field>

            <Field label="Description">
              <Textarea
                required value={form.description} onChange={set("description")}
                placeholder="What is this expense for?"
                rows={3}
                data-testid="description-input"
                className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700"
              />
            </Field>

            <Field label="Receipts / invoices (up to 5)">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label
                  className="flex items-center gap-3 px-4 py-3.5 rounded-md border border-dashed border-cream-200 bg-white hover:bg-cream-100 cursor-pointer transition-colors"
                  data-testid="receipt-upload-label"
                >
                  <Upload className="h-4 w-4 text-forest-700 shrink-0" />
                  <span className="text-sm text-ink-soft">Upload file(s)</span>
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                    disabled={files.length >= 5}
                    data-testid="receipt-file-input"
                  />
                </label>
                <label
                  className="flex items-center gap-3 px-4 py-3.5 rounded-md border border-dashed border-cream-200 bg-white hover:bg-cream-100 cursor-pointer transition-colors"
                  data-testid="receipt-camera-label"
                >
                  <Camera className="h-4 w-4 text-forest-700 shrink-0" />
                  <span className="text-sm text-ink-soft">Take a photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                    disabled={files.length >= 5}
                    data-testid="receipt-camera-input"
                  />
                </label>
              </div>
              <p className="mt-1.5 text-xs text-ink-mute">JPG, PNG, WEBP or PDF · max 10 MB each · up to 5 files</p>
              {files.length > 0 && (
                <ul className="mt-2.5 space-y-1.5" data-testid="receipt-file-list">
                  {files.map((f, idx) => (
                    <li
                      key={`${f.name}-${idx}`}
                      data-testid={`receipt-file-${idx}`}
                      className="flex items-center gap-2 bg-forest-50 border border-forest-100 rounded-md px-3 py-2"
                    >
                      <FileCheck className="h-3.5 w-3.5 text-forest-700 shrink-0" />
                      <span className="text-sm text-forest-800 flex-1 min-w-0 truncate">{f.name}</span>
                      <span className="text-[11px] text-ink-mute shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                      <button
                        type="button"
                        data-testid={`remove-receipt-${idx}`}
                        onClick={() => removeFile(idx)}
                        aria-label="Remove receipt"
                        className="text-ink-mute hover:text-rose-700 px-1 shrink-0"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                  <li className="text-[11px] text-ink-mute pl-1">{files.length}/5 files added</li>
                </ul>
              )}
            </Field>

            <div className="pt-2">
              <div className="flex items-baseline justify-between mb-1">
                <h2 className="font-heading text-lg text-forest-800">Approvers ({approvers.length})</h2>
                <span className="text-xs text-ink-mute">2–6 people · all must approve</span>
              </div>
              <p className="text-sm text-ink-soft mb-4">Add up to 6 unique approvers. Every one of them must approve for the expense to clear.</p>
              {templates.length > 0 && (
                <div className="mb-3" data-testid="template-list">
                  <div className="text-[11px] uppercase tracking-wider text-ink-mute mb-1.5">Shared templates · click to apply</div>
                  <div className="flex flex-wrap gap-2">
                    {templates.map((t) => (
                      <div key={t.id} className="inline-flex items-center gap-1 bg-forest-50 border border-forest-100 rounded-full pl-2.5 pr-1 py-0.5 text-xs">
                        <button type="button" onClick={() => applyTemplate(t)} data-testid={`apply-template-${t.name}`} className="text-forest-800 font-medium hover:underline">
                          {t.name} ({t.emails.length})
                        </button>
                        <button type="button" onClick={() => deleteTemplate(t.id)} aria-label="Delete template" className="text-ink-mute hover:text-rose-700 px-1">×</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-2.5" data-testid="approver-rows">
                {approvers.map((email, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      required type="email"
                      value={email}
                      onChange={(e) => {
                        const next = [...approvers]; next[idx] = e.target.value; setApprovers(next);
                      }}
                      placeholder={`Approver ${idx + 1} email`}
                      data-testid={`approver-${idx + 1}-input`}
                      className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700"
                    />
                    {approvers.length > 2 && (
                      <button
                        type="button"
                        data-testid={`remove-approver-${idx + 1}`}
                        onClick={() => setApprovers(approvers.filter((_, i) => i !== idx))}
                        className="text-xs text-ink-soft hover:text-rose-700 px-2 py-1"
                        aria-label="Remove approver"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {approvers.length < 6 && (
                <button
                  type="button"
                  data-testid="add-approver-btn"
                  onClick={() => setApprovers([...approvers, ""])}
                  className="mt-3 text-sm font-medium text-forest-700 hover:text-forest-800 inline-flex items-center gap-1"
                >
                  + Add approver
                </button>
              )}
              <button
                type="button"
                data-testid="save-template-btn"
                onClick={saveTemplate}
                className="mt-3 ml-4 text-sm font-medium text-ink-soft hover:text-forest-800"
              >
                Save as template
              </button>
            </div>

            <div className="pt-2">
              <h2 className="font-heading text-lg text-forest-800 mb-1">Send approved invoice to</h2>
              <p className="text-sm text-ink-soft mb-4">
                Once all approvers sign off, ALL receipts + a link to the audit trail are emailed here in one packaged email. Your email is CC&apos;d.
              </p>
              <Input
                required type="email"
                value={form.finance_email}
                onChange={set("finance_email")}
                placeholder="accounts@yourcharity.org"
                data-testid="finance-email-input"
                className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700"
              />
            </div>

            <div className="pt-2">
              <h2 className="font-heading text-lg text-forest-800 mb-1">
                Submit to accounts software <span className="text-xs font-normal text-ink-mute">(optional)</span>
              </h2>
              <p className="text-sm text-ink-soft mb-4">
                For tools like Dext, AutoEntry or Xero. Each receipt is sent as its own separate email (one invoice per email). You&apos;ll trigger this manually with a button on the approved expense.
              </p>
              <Input
                type="email"
                value={form.accounts_software_email}
                onChange={set("accounts_software_email")}
                placeholder="bills@yourcompany.dext.cc"
                data-testid="accounts-software-email-input"
                className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700"
              />
            </div>

            {error && (
              <div data-testid="form-error" className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <Button
                type="submit"
                disabled={busy}
                data-testid="submit-expense-button"
                className="bg-forest-700 hover:bg-forest-800 text-cream-50 h-11 px-6"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for approval"}
              </Button>
              <Button asChild type="button" variant="ghost" className="text-ink-soft">
                <Link to="/">Cancel</Link>
              </Button>
            </div>
          </form>
        </div>

        {/* Aside guidance */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-24 bg-white border border-cream-200 rounded-xl p-6">
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft font-semibold mb-2">How it works</div>
            <ol className="space-y-4 text-sm text-ink mt-3">
              <Step n={1} title="Submit">
                Upload your receipts (up to 5) and nominate 2–6 approvers.
              </Step>
              <Step n={2} title="Everyone signs off">
                All approvers must approve. If any one rejects, the expense is rejected.
              </Step>
              <Step n={3} title="Tamper-proof record">
                Every action is written to an append-only audit log — never edited, never deleted.
              </Step>
            </ol>
            <div className="mt-6 pt-5 border-t border-cream-200">
              <p className="text-[11px] text-ink-mute leading-relaxed">
                <strong className="text-ink">Phase 1:</strong> approvals are simulated via test buttons on the expense detail page. Email approvals arrive in Phase 2.
              </p>
            </div>
          </div>
        </aside>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-ink-soft">{label}</Label>
      {children}
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <li className="flex gap-3">
      <div className="h-6 w-6 shrink-0 rounded-full bg-forest-700 text-cream-50 grid place-items-center font-heading text-xs font-semibold">{n}</div>
      <div>
        <div className="font-medium text-forest-800">{title}</div>
        <p className="text-ink-soft text-[13px] leading-relaxed">{children}</p>
      </div>
    </li>
  );
}
